import { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  FileText,
  Plus,
  Download,
  Eye,
  Trash2,
  Calendar,
  BarChart3,
  PieChart,
  TrendingUp,
  Building2,
  Clock,
  Play,
  Settings2,
  FileSpreadsheet
} from 'lucide-react';
import { GlobalFilters } from '../components/GlobalFilters';

// Pre-built report templates
const REPORT_TEMPLATES = [
  {
    id: 'budget-variance',
    name: 'Budget Variance Report',
    description: 'Compare actual vs budgeted amounts with variance analysis',
    icon: TrendingUp,
    category: 'financial',
    metrics: ['budget', 'actual', 'variance', 'variance_pct']
  },
  {
    id: 'cashflow',
    name: 'Cashflow Summary',
    description: 'Monthly cashflow with forecasted vs actual',
    icon: BarChart3,
    category: 'financial',
    metrics: ['inflows', 'outflows', 'net_cashflow', 'cumulative']
  },
  {
    id: 'bu-performance',
    name: 'BU Performance',
    description: 'Performance metrics by business unit',
    icon: Building2,
    category: 'operational',
    metrics: ['revenue', 'expenses', 'margin', 'headcount']
  },
  {
    id: 'cost-center',
    name: 'Cost Center Analysis',
    description: 'Detailed cost center breakdown',
    icon: PieChart,
    category: 'financial',
    metrics: ['budget', 'actual', 'variance', 'ytd']
  },
  {
    id: 'executive-summary',
    name: 'Executive Summary',
    description: 'High-level KPIs and trends for leadership',
    icon: FileText,
    category: 'executive',
    metrics: ['revenue', 'net_income', 'margin', 'forecast_accuracy']
  }
];

// Sample saved reports
const SAMPLE_REPORTS = [
  {
    id: '1',
    name: 'Q3 Budget Review',
    template: 'budget-variance',
    created_at: '2025-12-01T10:00:00Z',
    created_by: 'John Admin',
    last_run: '2025-12-10T14:30:00Z',
    filters: { period: 'this_quarter', bu: 'all' }
  },
  {
    id: '2',
    name: 'Monthly Cashflow - December',
    template: 'cashflow',
    created_at: '2025-12-05T09:00:00Z',
    created_by: 'Jane Manager',
    last_run: '2025-12-12T11:00:00Z',
    filters: { period: 'this_month', bu: 'all' }
  }
];

// Available metrics for custom reports
const AVAILABLE_METRICS = [
  { id: 'revenue', label: 'Revenue', category: 'Financial' },
  { id: 'expenses', label: 'Expenses', category: 'Financial' },
  { id: 'net_income', label: 'Net Income', category: 'Financial' },
  { id: 'budget', label: 'Budget', category: 'Financial' },
  { id: 'actual', label: 'Actual', category: 'Financial' },
  { id: 'variance', label: 'Variance', category: 'Financial' },
  { id: 'variance_pct', label: 'Variance %', category: 'Financial' },
  { id: 'ytd', label: 'Year to Date', category: 'Financial' },
  { id: 'forecast', label: 'Forecast', category: 'Planning' },
  { id: 'headcount', label: 'Headcount', category: 'Operational' },
  { id: 'cost_per_unit', label: 'Cost per Unit', category: 'Operational' },
];

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-ZA', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const ReportsPage = () => {
  const [activeTab, setActiveTab] = useState('templates');
  const [savedReports, setSavedReports] = useState(SAMPLE_REPORTS);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [customReportDialog, setCustomReportDialog] = useState(false);
  const { isDark } = useTheme();
  const { hasPermission } = useAuth();

  const handleRunReport = (template) => {
    setSelectedTemplate(template);
    setDialogOpen(true);
  };

  const handleGenerateReport = (reportConfig) => {
    toast.info('Generating report...');
    setTimeout(() => {
      const newReport = {
        id: Date.now().toString(),
        name: reportConfig.name,
        template: reportConfig.templateId,
        created_at: new Date().toISOString(),
        created_by: 'Current User',
        last_run: new Date().toISOString(),
        filters: reportConfig.filters
      };
      setSavedReports(prev => [newReport, ...prev]);
      toast.success('Report generated successfully!');
      setDialogOpen(false);
      setActiveTab('saved');
    }, 1500);
  };

  const handleDeleteReport = (id) => {
    if (!window.confirm('Are you sure you want to delete this report?')) return;
    setSavedReports(prev => prev.filter(r => r.id !== id));
    toast.success('Report deleted');
  };

  const handleExportReport = (report, format) => {
    toast.info(`Exporting ${report.name} as ${format.toUpperCase()}...`);
    setTimeout(() => {
      toast.success('Export ready for download');
    }, 1000);
  };

  const handleSaveCustomReport = (config) => {
    const newReport = {
      id: Date.now().toString(),
      name: config.name,
      template: 'custom',
      created_at: new Date().toISOString(),
      created_by: 'Current User',
      last_run: new Date().toISOString(),
      filters: config.filters,
      metrics: config.selectedMetrics
    };
    setSavedReports(prev => [newReport, ...prev]);
    setCustomReportDialog(false);
    toast.success('Custom report created!');
    setActiveTab('saved');
  };

  return (
    <div className={`p-6 space-y-6 ${isDark ? 'text-white' : ''}`} data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className={`font-display text-3xl font-light ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Reports
          </h1>
          <p className={isDark ? 'text-zinc-400 mt-1' : 'text-slate-500 mt-1'}>
            Generate and customize financial reports
          </p>
        </div>
        {hasPermission('write') && (
          <Button 
            onClick={() => setCustomReportDialog(true)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm"
            data-testid="create-custom-report-btn"
          >
            <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Custom Report
          </Button>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={`p-1 rounded-sm ${isDark ? 'bg-zinc-800' : 'bg-slate-100'}`}>
          <TabsTrigger 
            value="templates" 
            className={`rounded-sm ${isDark ? 'data-[state=active]:bg-zinc-700' : 'data-[state=active]:bg-white'}`}
          >
            Report Templates
          </TabsTrigger>
          <TabsTrigger 
            value="saved" 
            className={`rounded-sm ${isDark ? 'data-[state=active]:bg-zinc-700' : 'data-[state=active]:bg-white'}`}
          >
            Saved Reports ({savedReports.length})
          </TabsTrigger>
        </TabsList>

        {/* Templates Tab */}
        <TabsContent value="templates" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {REPORT_TEMPLATES.map((template) => {
              const Icon = template.icon;
              return (
                <Card 
                  key={template.id}
                  className={`border shadow-none rounded-sm cursor-pointer transition-all hover:shadow-md ${
                    isDark ? 'bg-zinc-900 border-zinc-800 hover:border-zinc-700' : 'border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid={`template-${template.id}`}
                >
                  <CardContent className="p-6">
                    <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${
                      isDark ? 'bg-emerald-900/30' : 'bg-emerald-50'
                    }`}>
                      <Icon className="w-6 h-6 text-emerald-600" strokeWidth={1.5} />
                    </div>
                    <h3 className={`mt-4 font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {template.name}
                    </h3>
                    <p className={`text-sm mt-1 ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                      {template.description}
                    </p>
                    <div className="flex flex-wrap gap-1 mt-3">
                      {template.metrics.slice(0, 3).map((metric) => (
                        <span 
                          key={metric}
                          className={`px-2 py-0.5 text-xs rounded-sm ${
                            isDark ? 'bg-zinc-800 text-zinc-400' : 'bg-slate-100 text-slate-600'
                          }`}
                        >
                          {metric.replace('_', ' ')}
                        </span>
                      ))}
                      {template.metrics.length > 3 && (
                        <span className={`px-2 py-0.5 text-xs rounded-sm ${
                          isDark ? 'bg-zinc-800 text-zinc-400' : 'bg-slate-100 text-slate-600'
                        }`}>
                          +{template.metrics.length - 3} more
                        </span>
                      )}
                    </div>
                    <Button 
                      className="w-full mt-4 rounded-sm"
                      variant="outline"
                      onClick={() => handleRunReport(template)}
                    >
                      <Play className="w-4 h-4 mr-2" strokeWidth={1.5} />
                      Generate Report
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Saved Reports Tab */}
        <TabsContent value="saved" className="mt-6">
          <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
            <CardContent className="p-0">
              {savedReports.length > 0 ? (
                <div className={`divide-y ${isDark ? 'divide-zinc-800' : 'divide-slate-100'}`}>
                  {savedReports.map((report) => {
                    const template = REPORT_TEMPLATES.find(t => t.id === report.template);
                    const Icon = template?.icon || FileText;
                    return (
                      <div 
                        key={report.id}
                        className={`p-4 flex items-center justify-between ${
                          isDark ? 'hover:bg-zinc-800' : 'hover:bg-slate-50'
                        }`}
                        data-testid={`report-${report.id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${
                            isDark ? 'bg-emerald-900/30' : 'bg-emerald-50'
                          }`}>
                            <Icon className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
                          </div>
                          <div>
                            <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                              {report.name}
                            </p>
                            <div className={`flex items-center gap-3 text-sm ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                Last run: {formatDate(report.last_run)}
                              </span>
                              <span>by {report.created_by}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="rounded-sm"
                            onClick={() => toast.info('Opening report preview...')}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                          <Select onValueChange={(format) => handleExportReport(report, format)}>
                            <SelectTrigger className="w-[100px] rounded-sm h-9">
                              <Download className="w-4 h-4 mr-1" />
                              Export
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="xlsx">
                                <div className="flex items-center gap-2">
                                  <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
                                  Excel
                                </div>
                              </SelectItem>
                              <SelectItem value="pdf">
                                <div className="flex items-center gap-2">
                                  <FileText className="w-4 h-4 text-red-600" />
                                  PDF
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          {hasPermission('delete') && (
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="rounded-sm text-red-600"
                              onClick={() => handleDeleteReport(report.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className={`p-8 text-center ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                  <FileText className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-zinc-600' : 'text-slate-300'}`} strokeWidth={1.5} />
                  <p>No saved reports yet</p>
                  <p className="text-sm mt-1">Generate a report from a template to get started</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Generate Report Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle>Generate {selectedTemplate?.name}</DialogTitle>
          </DialogHeader>
          {selectedTemplate && (
            <GenerateReportForm 
              template={selectedTemplate}
              onGenerate={handleGenerateReport}
              onCancel={() => setDialogOpen(false)}
              isDark={isDark}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Custom Report Builder Dialog */}
      <Dialog open={customReportDialog} onOpenChange={setCustomReportDialog}>
        <DialogContent className="rounded-sm max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings2 className="w-5 h-5 text-emerald-600" />
              Custom Report Builder
            </DialogTitle>
          </DialogHeader>
          <CustomReportForm 
            onSave={handleSaveCustomReport}
            onCancel={() => setCustomReportDialog(false)}
            isDark={isDark}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

const GenerateReportForm = ({ template, onGenerate, onCancel, isDark }) => {
  const [name, setName] = useState(`${template.name} - ${new Date().toLocaleDateString()}`);
  const [period, setPeriod] = useState('this_year');
  const [bu, setBu] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onGenerate({
      name,
      templateId: template.id,
      filters: { period, bu }
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label>Report Name</Label>
        <Input 
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="rounded-sm"
        />
      </div>

      <div className="space-y-2">
        <Label>Time Period</Label>
        <GlobalFilters
          selectedPeriod={period}
          onPeriodChange={setPeriod}
          showBuFilter={false}
        />
      </div>

      <div className="space-y-2">
        <Label>Business Unit</Label>
        <GlobalFilters
          selectedBu={bu}
          onBuChange={setBu}
          showPeriodFilter={false}
        />
      </div>

      <DialogFooter className="gap-2">
        <Button type="button" variant="outline" onClick={onCancel} className="rounded-sm">
          Cancel
        </Button>
        <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm">
          <Play className="w-4 h-4 mr-2" />
          Generate
        </Button>
      </DialogFooter>
    </form>
  );
};

const CustomReportForm = ({ onSave, onCancel, isDark }) => {
  const [name, setName] = useState('');
  const [selectedMetrics, setSelectedMetrics] = useState([]);
  const [period, setPeriod] = useState('this_year');
  const [groupBy, setGroupBy] = useState('month');

  const toggleMetric = (metricId) => {
    setSelectedMetrics(prev => 
      prev.includes(metricId) 
        ? prev.filter(m => m !== metricId)
        : [...prev, metricId]
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Please enter a report name');
      return;
    }
    if (selectedMetrics.length === 0) {
      toast.error('Please select at least one metric');
      return;
    }
    onSave({
      name,
      selectedMetrics,
      filters: { period, groupBy }
    });
  };

  // Group metrics by category
  const metricsByCategory = AVAILABLE_METRICS.reduce((acc, metric) => {
    if (!acc[metric.category]) acc[metric.category] = [];
    acc[metric.category].push(metric);
    return acc;
  }, {});

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <Label>Report Name</Label>
        <Input 
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Monthly Revenue Analysis"
          className="rounded-sm"
        />
      </div>

      <div className="space-y-3">
        <Label>Select Metrics</Label>
        <div className={`p-4 rounded-sm border max-h-60 overflow-y-auto ${
          isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-slate-50 border-slate-200'
        }`}>
          {Object.entries(metricsByCategory).map(([category, metrics]) => (
            <div key={category} className="mb-4 last:mb-0">
              <p className={`text-xs font-medium uppercase tracking-wider mb-2 ${
                isDark ? 'text-zinc-500' : 'text-slate-400'
              }`}>
                {category}
              </p>
              <div className="grid grid-cols-2 gap-2">
                {metrics.map((metric) => (
                  <div key={metric.id} className="flex items-center space-x-2">
                    <Checkbox 
                      id={metric.id}
                      checked={selectedMetrics.includes(metric.id)}
                      onCheckedChange={() => toggleMetric(metric.id)}
                    />
                    <label htmlFor={metric.id} className="text-sm cursor-pointer">
                      {metric.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
          Selected: {selectedMetrics.length} metrics
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Time Period</Label>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="rounded-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="this_month">This Month</SelectItem>
              <SelectItem value="this_quarter">This Quarter</SelectItem>
              <SelectItem value="this_year">This Fiscal Year</SelectItem>
              <SelectItem value="last_year">Last Fiscal Year</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Group By</Label>
          <Select value={groupBy} onValueChange={setGroupBy}>
            <SelectTrigger className="rounded-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="day">Day</SelectItem>
              <SelectItem value="week">Week</SelectItem>
              <SelectItem value="month">Month</SelectItem>
              <SelectItem value="quarter">Quarter</SelectItem>
              <SelectItem value="year">Year</SelectItem>
              <SelectItem value="bu">Business Unit</SelectItem>
              <SelectItem value="cost_center">Cost Center</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <DialogFooter className="gap-2">
        <Button type="button" variant="outline" onClick={onCancel} className="rounded-sm">
          Cancel
        </Button>
        <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm">
          Create Report
        </Button>
      </DialogFooter>
    </form>
  );
};

export default ReportsPage;
